import {
    createContext,
    FunctionComponent,
    ReactNode,
    useEffect,
    useState
} from 'react';
import {
    AuthUser,
    fetchAuthSession,
    getCurrentUser,
    JWT
} from 'aws-amplify/auth';
import {AuthStatus} from '@flexinet/auth-components';


type AuthContextType = {
    user: AuthUser | undefined,
    idToken?: JWT | undefined,
    accessToken?: JWT | undefined,
    cognitoGroups?: string[] | undefined,
    role?: string | undefined,
    // FNP-1731: 'resolving' until both the user and the session lookups settle.
    status: AuthStatus,
}


// FNP-958: a context object and its provider component live together by
// design; splitting them would churn every import for a dev-only
// hot-reload nicety. Same suppression billing-app uses on the identical
// file (its AuthContext.tsx:25), so this matches platform precedent.
// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType>({
                                                              user: {} as AuthUser,
                                                              status: 'resolving',
                                                          });

interface IAuthProvider {
    children?: ReactNode
}

export const AuthProvider: FunctionComponent<IAuthProvider> = ({children}) => {

    const [user, setUser] = useState<AuthUser | undefined>();
    const [idToken, setIdToken] = useState<JWT | undefined>();
    const [accessToken, setAccessToken] = useState<JWT | undefined>();
    const [cognitoGroups, setCognitoGroups] = useState<string[] | undefined>();
    const [role, setRole] = useState<string | undefined>();
    const [userSettled, setUserSettled] = useState(false);
    const [sessionSettled, setSessionSettled] = useState(false);


    useEffect(() => {
        const fetchCognitoUser = async () => {

            try {
                const result = await getCurrentUser()
                console.debug('AUTH CONTEXT. FETCH USER', result)

                const {
                    username,
                    userId,
                    signInDetails
                } = result

                setUser({
                            username,
                            userId,
                            signInDetails
                        })


            } catch (error) {
                console.trace(
                    'AUTH CONTEXT. FETCH USER ERROR',
                    error
                );
            } finally {
                setUserSettled(true);
            }
        };

        fetchCognitoUser();

    }, []);


    useEffect(() => {
        const fetchSession = async () => {

            try {
                const {
                    accessToken,
                    idToken
                } = (await fetchAuthSession()).tokens ?? {};

                setIdToken(idToken);
                setAccessToken(accessToken);
                if (typeof idToken !== 'undefined'
                    && idToken.payload['cognito:groups'] !== undefined
                    && Array.isArray(idToken.payload['cognito:groups'])
                    && idToken.payload['cognito:groups'].length > 0) {
                    setCognitoGroups(idToken.payload['cognito:groups'] as string[]);
                }

            } catch (error) {
                console.trace(
                    'AUTH CONTEXT. FETCH SESSION ERROR',
                    error
                );
            } finally {
                setSessionSettled(true);
            }

        };

        fetchSession();
    }, []);

    useEffect(() => {

        if (typeof idToken !== 'undefined'
            && idToken.payload['cognito:groups'] !== undefined
            && Array.isArray(idToken.payload['cognito:groups'])
            && idToken.payload['cognito:groups'].length > 0) {


            const adminItem = idToken.payload['cognito:groups'].find((item: unknown) => item === 'admin')
            if (adminItem) {
                setRole('Admin')
            }

        }

    }, [idToken])

    let status: AuthStatus = 'resolving';
    if (userSettled && sessionSettled) {
        status = user !== undefined && idToken !== undefined ? 'authenticated' : 'anonymous';
    }


    return (
        <AuthContext.Provider value={{
            user,
            idToken,
            accessToken,
            cognitoGroups,
            role,
            status
        }}>
            {children}
        </AuthContext.Provider>
    );
};